import { useRef } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PRODUCTS } from "@/data/content";

const EASE = [0.16, 1, 0.3, 1];

function ProductCard({ product, aspectClass }) {
  const ref = useRef(null);

  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    el.style.setProperty("--mx", `${e.clientX - r.left}px`);
    el.style.setProperty("--my", `${e.clientY - r.top}px`);
  };

  return (
    <motion.a
      ref={ref}
      onMouseMove={onMove}
      href={product.url}
      target="_blank"
      rel="noopener noreferrer"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE }}
      className="group relative block overflow-hidden border border-white/10 transition-colors duration-500 hover:border-[#0033FF]/70"
      data-testid={`product-card-${product.id}`}
    >
      <div className="overflow-hidden">
        <img
          src={product.image}
          alt={product.name}
          loading="lazy"
          className={`w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110 ${aspectClass}`}
        />
      </div>

      <div className="pointer-events-none absolute inset-0 bg-black/20" />
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-black/10" />
      <div
        className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-500 group-hover:opacity-100"
        style={{
          background:
            "radial-gradient(500px circle at var(--mx, 50%) var(--my, 50%), rgba(0,51,255,0.22), transparent 65%)",
        }}
      />

      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-6 p-6 md:p-10">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-[#0033FF]">
            {product.index} — {product.tag}
          </p>
          <h3 className="mt-3 font-display text-3xl font-black uppercase leading-none tracking-tight md:text-5xl">
            {product.name}
          </h3>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-[#A0A0A0]">
            {product.desc}
          </p>
        </div>
        <span className="flex h-12 w-12 shrink-0 items-center justify-center border border-white/20 transition-colors duration-300 group-hover:border-[#0033FF] group-hover:bg-[#0033FF] md:h-16 md:w-16">
          <ArrowUpRight className="h-5 w-5 transition-transform duration-300 group-hover:-translate-y-1 group-hover:translate-x-1 md:h-6 md:w-6" />
        </span>
      </div>
    </motion.a>
  );
}

export default function Products() {
  return (
    <section
      id="products"
      className="mx-auto max-w-[1440px] px-6 py-28 md:px-12 md:py-40"
      data-testid="products-section"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-20 grid items-end gap-8 md:grid-cols-12"
      >
        <div className="md:col-span-8">
          <p className="font-mono text-xs uppercase tracking-[0.35em] text-[#0033FF]">
            ( 02 — Products )
          </p>
          <h2 className="mt-6 font-display text-4xl font-black uppercase leading-none tracking-tight md:text-6xl">
            Our Apps
          </h2>
        </div>
        <p className="max-w-sm text-base leading-relaxed text-[#A0A0A0] md:col-span-4">
          Software we design, build, and maintain — tools born from real
          consulting work, now products in their own right.
        </p>
      </motion.div>

      <div className="grid gap-8 md:grid-cols-12 md:gap-10">
        <div className="md:col-span-7">
          <ProductCard product={PRODUCTS[0]} aspectClass="aspect-video" />
        </div>
        <div className="md:col-span-5">
          <ProductCard product={PRODUCTS[1]} aspectClass="aspect-video md:aspect-[4/3] lg:aspect-auto lg:h-full" />
        </div>
        <div className="md:col-span-5">
          <ProductCard product={PRODUCTS[2]} aspectClass="aspect-video md:aspect-[4/3] lg:aspect-auto lg:h-full" />
        </div>
        <div className="md:col-span-7">
          <ProductCard product={PRODUCTS[3]} aspectClass="aspect-video" />
        </div>
      </div>
    </section>
  );
}
